// features/inventory/pages/BulkTransferPage.tsx
/**
 * Bulk Transfer Page — Upload Excel files to create stock transfers in bulk.
 *
 * Route: /inventory/transfers/bulk
 *
 * Features:
 * - Branch selection with visual direction indicator
 * - Template download with instructions
 * - Drag-and-drop file upload with validation
 * - Real-time progress tracking with step indicators
 * - Stock availability validation per product
 * - Comprehensive error reporting with suggested fixes
 * - Success summary with transfer tracking and total quantity
 * - Download error report for failed rows
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { BulkUploadProgress } from '@/components/bulk-upload/BulkUploadProgress';
import { BulkUploadErrors, BulkUploadErrorDetail } from '@/components/bulk-upload/BulkUploadErrors';
import {
    downloadTransferTemplate,
    bulkUploadTransfer,
    BulkTransferResult,
} from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    Upload,
    Download,
    FileSpreadsheet,
    Store,
    CheckCircle,
    AlertTriangle,
    Info,
    ArrowRightLeft,
    Package,
    FileText,
    ArrowRight,
    ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BulkTransferPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [step, setStep] = useState<'select' | 'upload' | 'processing' | 'complete'>('select');
    const [fromBranchId, setFromBranchId] = useState('');
    const [toBranchId, setToBranchId] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [transferResult, setTransferResult] = useState<BulkTransferResult | null>(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Progress state
    const [bulkUploadState, setBulkUploadState] = useState<{
        isOpen: boolean;
        status: 'processing' | 'completed' | 'failed';
        progress: number;
        steps: Array<{
            id: string;
            label: string;
            icon: React.ElementType;
            status: 'pending' | 'active' | 'completed' | 'error';
        }>;
        result: BulkTransferResult | null;
        errorMessage?: string;
    }>({
        isOpen: false,
        status: 'processing',
        progress: 0,
        steps: [
            {
                id: 'validate',
                label: 'Validating file & branches',
                icon: FileText,
                status: 'pending',
            },
            {
                id: 'stock',
                label: 'Checking stock availability',
                icon: Package,
                status: 'pending',
            },
            {
                id: 'transfer',
                label: 'Creating transfer records',
                icon: ArrowRightLeft,
                status: 'pending',
            },
            {
                id: 'finalize',
                label: 'Finalizing',
                icon: CheckCircle,
                status: 'pending',
            },
        ],
        result: null,
        errorMessage: undefined,
    });

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-bulk-transfer', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        code?: string;
    }>;
    const isSingleStore = branchesList.length === 0;
    const getBranchName = (id: string) =>
        branchesList.find((b: any) => b.id === id)?.name || 'Unknown';
    const canProceed =
        fromBranchId && toBranchId && fromBranchId !== toBranchId;

    // Mutations
    const transferMutation = useMutation({
        mutationFn: async (file: File) => {
            setStep('processing');
            setBulkUploadState((prev) => ({
                ...prev,
                isOpen: true,
                status: 'processing',
                progress: 0,
                steps: prev.steps.map((s) => ({
                    ...s,
                    status: 'pending' as const,
                })),
                result: null,
                errorMessage: undefined,
            }));

            const updateProgress = (stepId: string, progress: number) => {
                setBulkUploadState((prev) => ({
                    ...prev,
                    progress,
                    steps: prev.steps.map((s) => {
                        if (s.id === stepId)
                            return {
                                ...s,
                                status: 'active' as const,
                            };
                        const si = prev.steps.findIndex(
                            (ps) => ps.id === s.id
                        );
                        const ai = prev.steps.findIndex(
                            (ps) => ps.id === stepId
                        );
                        if (si < ai)
                            return {
                                ...s,
                                status: 'completed' as const,
                            };
                        return s;
                    }),
                }));
            };

            updateProgress('validate', 15);
            await new Promise((r) => setTimeout(r, 400));
            updateProgress('stock', 45);

            const result = await bulkUploadTransfer(
                user!.tenantId,
                fromBranchId,
                toBranchId,
                file
            );

            updateProgress('transfer', 75);
            await new Promise((r) => setTimeout(r, 300));
            updateProgress('finalize', 95);

            return result;
        },
        onSuccess: (result) => {
            const hasErrors =
                result.hasErrors || result.errorCount > 0;
            setBulkUploadState((prev) => ({
                ...prev,
                progress: 100,
                status: hasErrors ? 'failed' : 'completed',
                steps: prev.steps.map((s) => ({
                    ...s,
                    status:
                        s.id === 'finalize' && hasErrors
                            ? ('error' as const)
                            : ('completed' as const),
                })),
                result,
            }));
            setStep('complete');
            setTransferResult(result);
            queryClient.invalidateQueries({
                queryKey: ['inventory'],
            });
            queryClient.invalidateQueries({
                queryKey: ['inventory-transfers'],
            });
            queryClient.invalidateQueries({
                queryKey: ['transfers-dash'],
            });
            addToast({
                type: hasErrors ? 'warning' : 'success',
                title: 'Bulk transfer completed',
                message: `${result.successCount} transferred, ${result.errorCount} errors, ${result.totalQuantityTransferred?.toLocaleString() || 0} total quantity`,
            });
        },
        onError: (err: any) => {
            setStep('upload');
            setBulkUploadState((prev) => ({
                ...prev,
                progress: 100,
                status: 'failed',
                errorMessage:
                    err?.message ||
                    'An unexpected error occurred.',
                steps: prev.steps.map((s) => ({
                    ...s,
                    status:
                        s.status === 'active'
                            ? ('error' as const)
                            : s.status,
                })),
                result: null,
            }));
            addToast({
                type: 'error',
                title: 'Transfer failed',
                message: err?.message,
            });
        },
    });

    // Handlers
    const handleFileSelect = useCallback(
        (file: File) => {
            const ext =
                '.' + file.name.split('.').pop()?.toLowerCase();
            if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
                addToast({
                    type: 'error',
                    title: 'Invalid file',
                    message:
                        'Please upload an Excel or CSV file.',
                });
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                addToast({
                    type: 'error',
                    title: 'File too large',
                    message: 'Maximum file size is 10MB.',
                });
                return;
            }
            setSelectedFile(file);
            setStep('upload');
        },
        [addToast]
    );

    const handleDownloadTemplate = async () => {
        try {
            const blob = await downloadTransferTemplate(
                user!.tenantId
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download =
                'inventory-bulk-transfer-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
            addToast({
                type: 'success',
                title: 'Template downloaded',
            });
        } catch {
            addToast({
                type: 'error',
                title: 'Download failed',
            });
        }
    };

    // Single store mode
    if (isSingleStore) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
                <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/transfers'
                                    )
                                }
                                className="p-2 rounded-xl hover:bg-slate-100"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg shadow-purple-500/25">
                                <ArrowRightLeft className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    Bulk Transfer
                                </h1>
                                <p className="text-sm text-slate-500">
                                    Single Store Mode
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
                    <EmptyState
                        icon={
                            <ArrowRightLeft className="w-16 h-16" />
                        }
                        title="Bulk Transfers Unavailable"
                        description="Bulk stock transfers require at least 2 branches. Configure additional branches in Settings."
                        action={{
                            label: 'Configure Branches',
                            onClick: () =>
                                navigate('/settings'),
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/transfers'
                                    )
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg shadow-purple-500/25">
                                <ArrowRightLeft className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    Bulk Transfer
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {branchesList.length} branches
                                    available
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 space-y-6">
                {/* STEP 1: Select Branches */}
                {step === 'select' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 ring-1 ring-purple-200/50">
                                        <Store className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">
                                            Select Branches
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Choose source and
                                            destination for the
                                            bulk transfer
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* From Branch */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            From Branch (Source)
                                        </label>
                                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                            {branchesList.map(
                                                (b: any) => (
                                                    <button
                                                        key={
                                                            b.id
                                                        }
                                                        onClick={() =>
                                                            setFromBranchId(
                                                                b.id
                                                            )
                                                        }
                                                        className={clsx(
                                                            'w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                                                            fromBranchId ===
                                                                b.id
                                                                ? 'border-purple-400 bg-purple-50 ring-2 ring-purple-500/20'
                                                                : 'border-slate-200 hover:border-purple-300',
                                                            b.id ===
                                                            toBranchId &&
                                                            'opacity-50 cursor-not-allowed'
                                                        )}
                                                        type="button"
                                                        disabled={
                                                            b.id ===
                                                            toBranchId
                                                        }
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className={clsx(
                                                                    'p-2 rounded-lg',
                                                                    fromBranchId ===
                                                                        b.id
                                                                        ? 'bg-purple-100'
                                                                        : 'bg-slate-100'
                                                                )}
                                                            >
                                                                <Store
                                                                    className={clsx(
                                                                        'w-5 h-5',
                                                                        fromBranchId ===
                                                                            b.id
                                                                            ? 'text-purple-600'
                                                                            : 'text-slate-400'
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-sm">
                                                                    {
                                                                        b.name
                                                                    }
                                                                </p>
                                                                {b.code && (
                                                                    <p className="text-xs text-slate-400">
                                                                        {
                                                                            b.code
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {fromBranchId ===
                                                            b.id && (
                                                                <CheckCircle className="w-5 h-5 text-purple-500 mt-2" />
                                                            )}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* To Branch */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            To Branch
                                            (Destination)
                                        </label>
                                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                            {branchesList.map(
                                                (b: any) => (
                                                    <button
                                                        key={
                                                            b.id
                                                        }
                                                        onClick={() =>
                                                            setToBranchId(
                                                                b.id
                                                            )
                                                        }
                                                        className={clsx(
                                                            'w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                                                            toBranchId ===
                                                                b.id
                                                                ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-500/20'
                                                                : 'border-slate-200 hover:border-indigo-300',
                                                            b.id ===
                                                            fromBranchId &&
                                                            'opacity-50 cursor-not-allowed'
                                                        )}
                                                        type="button"
                                                        disabled={
                                                            b.id ===
                                                            fromBranchId
                                                        }
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className={clsx(
                                                                    'p-2 rounded-lg',
                                                                    toBranchId ===
                                                                        b.id
                                                                        ? 'bg-indigo-100'
                                                                        : 'bg-slate-100'
                                                                )}
                                                            >
                                                                <Store
                                                                    className={clsx(
                                                                        'w-5 h-5',
                                                                        toBranchId ===
                                                                            b.id
                                                                            ? 'text-indigo-600'
                                                                            : 'text-slate-400'
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-sm">
                                                                    {
                                                                        b.name
                                                                    }
                                                                </p>
                                                                {b.code && (
                                                                    <p className="text-xs text-slate-400">
                                                                        {
                                                                            b.code
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {toBranchId ===
                                                            b.id && (
                                                                <CheckCircle className="w-5 h-5 text-indigo-500 mt-2" />
                                                            )}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Transfer Direction */}
                                {canProceed && (
                                    <motion.div
                                        initial={{
                                            opacity: 0,
                                            scale: 0.95,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            scale: 1,
                                        }}
                                        className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl flex items-center justify-center gap-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="px-4 py-2 bg-purple-100 rounded-xl">
                                                <span className="font-semibold text-purple-700">
                                                    {getBranchName(
                                                        fromBranchId
                                                    )}
                                                </span>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-purple-400" />
                                            <ArrowRightLeft className="w-5 h-5 text-purple-500" />
                                            <ChevronRight className="w-5 h-5 text-indigo-400" />
                                            <div className="px-4 py-2 bg-indigo-100 rounded-xl">
                                                <span className="font-semibold text-indigo-700">
                                                    {getBranchName(
                                                        toBranchId
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Upload Section */}
                        {canProceed && (
                            <Card>
                                <CardContent className="p-6 space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 ring-1 ring-purple-200/50">
                                            <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">
                                                Upload Transfer
                                                File
                                            </h3>
                                            <p className="text-xs text-slate-500">
                                                Excel file with
                                                products and
                                                quantities to
                                                transfer
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={
                                                handleDownloadTemplate
                                            }
                                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                            type="button"
                                        >
                                            <Download className="w-4 h-4" />{' '}
                                            Download Template
                                        </button>
                                        <span className="text-xs text-slate-400">
                                            Fill with products and
                                            quantities, then upload
                                        </span>
                                    </div>

                                    <div
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setIsDragging(false);
                                            const f =
                                                e.dataTransfer
                                                    .files?.[0];
                                            if (f)
                                                handleFileSelect(
                                                    f
                                                );
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setIsDragging(true);
                                        }}
                                        onDragLeave={() =>
                                            setIsDragging(false)
                                        }
                                        className={clsx(
                                            'border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer',
                                            isDragging
                                                ? 'border-purple-400 bg-purple-50/50'
                                                : 'border-slate-300 hover:border-purple-400 hover:bg-slate-50'
                                        )}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            className="hidden"
                                            onChange={(e) => {
                                                const f =
                                                    e.target
                                                        .files?.[0];
                                                if (f) {
                                                    handleFileSelect(
                                                        f
                                                    );
                                                    e.target.value =
                                                        '';
                                                }
                                            }}
                                        />
                                        <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                                            <Upload className="w-7 h-7 text-purple-600" />
                                        </div>
                                        <h3 className="text-base font-bold text-slate-700 mb-1">
                                            {isDragging
                                                ? 'Drop your file here'
                                                : 'Upload Transfer File'}
                                        </h3>
                                        <p className="text-sm text-slate-400">
                                            Excel (.xlsx, .xls) or
                                            CSV · Max 10MB
                                        </p>
                                    </div>

                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                                        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-blue-700">
                                                How this works
                                            </p>
                                            <ul className="text-xs text-blue-600 mt-1 space-y-1">
                                                <li>
                                                    · Products
                                                    matched by
                                                    SKU, barcode,
                                                    or name
                                                </li>
                                                <li>
                                                    · Stock
                                                    availability
                                                    checked in
                                                    source branch
                                                </li>
                                                <li>
                                                    · Failed rows
                                                    are skipped
                                                    with error
                                                    details
                                                </li>
                                                <li>
                                                    · A single
                                                    transfer
                                                    record is
                                                    created for
                                                    tracking
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </motion.div>
                )}

                {/* STEP 2: Confirm Upload */}
                {step === 'upload' && selectedFile && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 ring-1 ring-purple-200/50">
                                        <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">
                                            Confirm Bulk Transfer
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Review details before
                                            processing
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-xl">
                                        <p className="text-xs text-slate-400 uppercase mb-1">
                                            File
                                        </p>
                                        <p className="font-semibold text-sm truncate">
                                            {selectedFile.name}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            {(
                                                selectedFile.size /
                                                1024
                                            ).toFixed(1)}{' '}
                                            KB
                                        </p>
                                    </div>
                                    <div className="p-4 bg-purple-50 rounded-xl">
                                        <p className="text-xs text-purple-500 uppercase mb-1">
                                            From
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <Store className="w-4 h-4 text-purple-500" />
                                            <p className="font-semibold text-sm">
                                                {getBranchName(
                                                    fromBranchId
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-indigo-50 rounded-xl">
                                        <p className="text-xs text-indigo-500 uppercase mb-1">
                                            To
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <Store className="w-4 h-4 text-indigo-500" />
                                            <p className="font-semibold text-sm">
                                                {getBranchName(
                                                    toBranchId
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() =>
                                            transferMutation.mutate(
                                                selectedFile
                                            )
                                        }
                                        disabled={
                                            transferMutation.isPending
                                        }
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all font-semibold shadow-lg shadow-purple-500/25 disabled:opacity-50"
                                        type="button"
                                    >
                                        {transferMutation.isPending ? (
                                            <Spinner
                                                size="xs"
                                                variant="white"
                                            />
                                        ) : (
                                            <ArrowRightLeft className="w-4 h-4" />
                                        )}
                                        Process Bulk Transfer
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setStep('select');
                                        }}
                                        className="px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 3: Complete */}
                {step === 'complete' && transferResult && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <Card
                            className={clsx(
                                'border-2',
                                transferResult.hasErrors
                                    ? 'border-amber-200'
                                    : 'border-emerald-200'
                            )}
                        >
                            <CardContent className="p-6 text-center">
                                <div
                                    className={clsx(
                                        'w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4',
                                        transferResult.hasErrors
                                            ? 'bg-amber-100'
                                            : 'bg-emerald-100'
                                    )}
                                >
                                    {transferResult.hasErrors ? (
                                        <AlertTriangle className="w-8 h-8 text-amber-600" />
                                    ) : (
                                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                                    )}
                                </div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    {transferResult.hasErrors
                                        ? 'Bulk Transfer Completed with Issues'
                                        : 'Bulk Transfer Successful!'}
                                </h2>
                                <p className="text-sm text-slate-500 mt-2">
                                    {transferResult.successCount}{' '}
                                    of {transferResult.totalRows}{' '}
                                    products transferred
                                    {transferResult.totalQuantityTransferred >
                                        0 && (
                                            <>
                                                {' '}
                                                ·{' '}
                                                {transferResult.totalQuantityTransferred.toLocaleString()}{' '}
                                                total quantity
                                            </>
                                        )}
                                </p>
                            </CardContent>
                        </Card>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                                    Total Rows
                                </p>
                                <p className="text-2xl font-bold text-slate-900">
                                    {transferResult.totalRows}
                                </p>
                            </div>
                            <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-center">
                                <p className="text-[10px] text-emerald-600 uppercase tracking-wider">
                                    Transferred
                                </p>
                                <p className="text-2xl font-bold text-emerald-700">
                                    {
                                        transferResult.successCount
                                    }
                                </p>
                            </div>
                            <div className="bg-rose-50 rounded-2xl border border-rose-200 p-4 text-center">
                                <p className="text-[10px] text-rose-600 uppercase tracking-wider">
                                    Errors
                                </p>
                                <p className="text-2xl font-bold text-rose-700">
                                    {transferResult.errorCount}
                                </p>
                            </div>
                            <div className="bg-purple-50 rounded-2xl border border-purple-200 p-4 text-center">
                                <p className="text-[10px] text-purple-600 uppercase tracking-wider">
                                    Total Qty
                                </p>
                                <p className="text-2xl font-bold text-purple-700">
                                    {transferResult.totalQuantityTransferred?.toLocaleString() ||
                                        0}
                                </p>
                            </div>
                        </div>

                        {transferResult.hasErrors && (
                            <div className="flex justify-center">
                                <button
                                    onClick={() =>
                                        setShowErrorDetails(true)
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors text-sm font-semibold"
                                    type="button"
                                >
                                    <AlertTriangle className="w-4 h-4" />
                                    View Error Details (
                                    {transferResult.errorCount})
                                </button>
                            </div>
                        )}

                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => {
                                    setStep('select');
                                    setSelectedFile(null);
                                    setTransferResult(null);
                                }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Upload className="w-4 h-4" />{' '}
                                New Transfer
                            </button>
                            {transferResult.transferId && (
                                <button
                                    onClick={() =>
                                        navigate(
                                            `/inventory/transfers`
                                        )
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 transition-all font-semibold shadow-lg text-sm"
                                    type="button"
                                >
                                    View Transfers{' '}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/transfers'
                                    )
                                }
                                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                All Transfers
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Progress & Error Modals */}
            <BulkUploadProgress
                isOpen={
                    bulkUploadState.isOpen && !showErrorDetails
                }
                onClose={() => {
                    if (
                        bulkUploadState.status !== 'processing'
                    ) {
                        setBulkUploadState((prev) => ({
                            ...prev,
                            isOpen: false,
                        }));
                        if (
                            bulkUploadState.result?.hasErrors
                        )
                            setShowErrorDetails(true);
                    }
                }}
                steps={bulkUploadState.steps}
                progress={bulkUploadState.progress}
                status={bulkUploadState.status}
                stats={{
                    totalRows:
                        bulkUploadState.result?.totalRows ||
                        0,
                    successCount:
                        bulkUploadState.result
                            ?.successCount || 0,
                    errorCount:
                        bulkUploadState.result?.errorCount ||
                        0,
                    categoriesCreated: 0,
                }}
                errorMessage={bulkUploadState.errorMessage}
            />

            <BulkUploadErrors
                isOpen={showErrorDetails}
                onClose={() => setShowErrorDetails(false)}
                errors={
                    (transferResult?.errors ||
                        []) as BulkUploadErrorDetail[]
                }
                totalRows={transferResult?.totalRows || 0}
                successCount={
                    transferResult?.successCount || 0
                }
                errorCount={transferResult?.errorCount || 0}
            />
        </div>
    );
}